import { z } from 'zod';
import { isoTimestamp } from './common';
import { resourceNameSchema } from './tools';

// --- Prompt library ---

const descriptionSchema = z.string().trim().max(2000);
export const promptContentSchema = z.string().max(100_000, 'The prompt is too long');
const tagSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9][a-z0-9 _-]{0,39}$/i, 'Tags are 1–40 letters, digits, spaces, - or _');

export const createPromptBlockRequestSchema = z.object({
  name: resourceNameSchema,
  description: descriptionSchema.default(''),
  content: promptContentSchema,
  tags: z.array(tagSchema).max(20).default([]),
});
export type CreatePromptBlockRequest = z.infer<typeof createPromptBlockRequestSchema>;

export const updatePromptBlockRequestSchema = z
  .object({
    description: descriptionSchema.optional(),
    content: promptContentSchema.optional(),
    tags: z.array(tagSchema).max(20).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdatePromptBlockRequest = z.infer<typeof updatePromptBlockRequestSchema>;

export const promptBlockDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  content: z.string(),
  tags: z.array(z.string()),
  /** Increases on every content change; older content is kept as versions. */
  version: z.number().int(),
  /** `{{name}}` placeholders in the content (filled from request context or prompt variables). */
  variables: z.array(z.string()),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type PromptBlockDto = z.infer<typeof promptBlockDtoSchema>;

export const promptBlockVersionDtoSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  content: z.string(),
  createdByUserId: z.uuid().nullable(),
  createdAt: isoTimestamp,
});
export type PromptBlockVersionDto = z.infer<typeof promptBlockVersionDtoSchema>;

const PROMPT_VARIABLE = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;

/** The `{{name}}` variables a prompt uses, in first-use order. */
export function promptVariables(content: string): string[] {
  return [...new Set([...content.matchAll(PROMPT_VARIABLE)].map((m) => m[1]!))];
}

/** Fills `{{name}}` variables; unknown ones stay as written (same rules as the gateway). */
export function renderPromptVariables(
  content: string,
  values: Record<string, string | number | boolean>,
): string {
  return content.replace(PROMPT_VARIABLE, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(values, name)
      ? String(values[name]).replace(/\{\{/g, '{ {').replace(/\}\}/g, '} }')
      : match,
  );
}

// --- Request context ---

export const requestContextKeySchema = z
  .string()
  .regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, 'Keys are identifiers like user_tier');

/**
 * Per-request values (like Mastra's RequestContext): fill `{{key}}` variables in an agent's system
 * prompt, and `{{context.key}}` in workflow templates.
 */
export const requestContextValuesSchema = z
  .record(
    requestContextKeySchema,
    z.union([z.string().max(10_000), z.number().finite(), z.boolean()]),
  )
  .refine((v) => Object.keys(v).length <= 50, { message: 'At most 50 values' });
export type RequestContextValues = z.infer<typeof requestContextValuesSchema>;

export const createRequestContextRequestSchema = z.object({
  name: resourceNameSchema,
  description: descriptionSchema.default(''),
  values: requestContextValuesSchema,
});
export type CreateRequestContextRequest = z.infer<typeof createRequestContextRequestSchema>;

export const updateRequestContextRequestSchema = z
  .object({
    description: descriptionSchema.optional(),
    values: requestContextValuesSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateRequestContextRequest = z.infer<typeof updateRequestContextRequestSchema>;

export const requestContextDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  values: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type RequestContextDto = z.infer<typeof requestContextDtoSchema>;
